import { measureText } from '@chronosphereio/chrono-ui';
import { formatValue } from '@/core';
import { PieChartBaseProps } from './PieChartBase';

const xMoreRegex = /^\d+\s+more$/; // matches text like "5 more"

type FormatTooltipArgs = Pick<
  PieChartBaseProps,
  'labelFormat' | 'unit' | 'aggregatedSeries' | 'percentDecimalPlaces'
> & {
  params: {
    color: string;
    name: string;
    value: [string, number | null];
    percent: number;
  };
  valuesSum: number;
};

export const formatTooltip = ({
  params,
  labelFormat,
  unit,
  aggregatedSeries,
  valuesSum,
  percentDecimalPlaces,
}: FormatTooltipArgs) => {
  const isAggregatedSlice = xMoreRegex.test(params.name);
  const tooltipContent = isAggregatedSlice
    ? generateAggregatedSliceTooltipContent({ labelFormat, unit, aggregatedSeries, valuesSum, percentDecimalPlaces })
    : generateSingleSliceTooltipContent({
        params,
        labelFormat,
        unit,
        value: params.value[1],
        percentage: params.percent,
      });

  return `
    <div style="max-width: 650px;">
      ${tooltipContent}
    </div>
  `;
};

type GetDisplayValueArgs = Pick<FormatTooltipArgs, 'labelFormat' | 'unit'> & {
  value: number | null;
  percentage: number;
};

const getDisplayValue = ({ labelFormat, unit, value, percentage }: GetDisplayValueArgs) => {
  if (value === null) {
    return 'No data';
  }
  const formattedValue = formatValue(value, unit);
  return labelFormat === 'Percent' ? `${percentage}% (${formattedValue})` : `${formattedValue} (${percentage}%)`;
};

const generateAggregatedSliceTooltipContent = ({
  labelFormat,
  unit,
  aggregatedSeries,
  valuesSum,
  percentDecimalPlaces,
}: Omit<FormatTooltipArgs, 'params'>) => {
  const seriesRows = aggregatedSeries
    .slice(0, 10) // Show only first 10 aggregated series in tooltip
    .map((series) => {
      const seriesName = series[0];
      const seriesValue = series[1];
      // if valuesSum is 0, set percentage to 0 so we don't end up dividing by 0
      const percentage =
        valuesSum === 0 ? 0 : Number((((seriesValue ?? 0) / valuesSum) * 100).toFixed(percentDecimalPlaces));
      return `
        <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
          <strong style="margin-right: 16px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;">${seriesName}</strong>
          <div>${getDisplayValue({ labelFormat, unit, value: seriesValue, percentage })}</div>
        </div>
      `;
    })
    .join('');
  const seriesPaginationInfo = `
    <div style="margin: 8px 0; border-bottom: 1px solid #DCDFEA;"></div>
    <p style="margin: 0; text-align: right;">Showing ${Math.min(aggregatedSeries.length, 10)} of ${aggregatedSeries.length} series</p>`;
  return `${seriesRows}${seriesPaginationInfo}`;
};

type generateSingleSliceTooltipContentArgs = Pick<FormatTooltipArgs, 'params' | 'labelFormat' | 'unit'> & {
  value: number | null;
  percentage: number;
};

const generateSingleSliceTooltipContent = ({
  params,
  labelFormat,
  unit,
  value,
  percentage,
}: generateSingleSliceTooltipContentArgs) => {
  const displayText = getDisplayValue({ labelFormat, unit, value, percentage });
  return `
    <div style="display: inline-flex; align-items: center; width: 100%">
      <span style="width: 12px; height: 12px; background-color: ${params.color}; margin-right: 8px; flex-shrink: 0"></span>
      <strong style="margin-right: 16px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;">${params.name}</strong> ${displayText}
    </div>`;
};

/**
 * Returns the label's width in pixels. Used to determine whether to show or hide a label within a pie slice.
 */
const measureLabelWidth = (label: string) => {
  return measureText(label)?.width ?? label.length * 8; // Fall back to 8px per character if the browser does not support the context API
};

export const formatValueLabel = (
  params: { value: [string, number | null]; percent: number },
  labelFormat: PieChartBaseProps['labelFormat'],
  unit: PieChartBaseProps['unit']
) => {
  const valueLabel = typeof params.value[1] === 'number' ? formatValue(params.value[1], unit) : 'No data';
  const percentageLabel = `${params.percent}%`;

  let labelText = percentageLabel;
  let labelWidth = measureLabelWidth(percentageLabel);
  if (labelFormat === 'Both') {
    labelText = `${valueLabel}\n${percentageLabel}`;
    // get the longer of the two labels since they render on separate lines
    const longerLabel = valueLabel.length >= percentageLabel.length ? valueLabel : percentageLabel;
    labelWidth = measureLabelWidth(longerLabel);
  } else if (labelFormat === 'Value') {
    labelText = valueLabel;
    labelWidth = measureLabelWidth(valueLabel);
  }

  const sliceAngle = params.percent * 360;
  // arc length = 2πr(Θ/360) where r is the radius of the circle and Θ is the angle
  const availableArcLength = 2 * Math.PI * 0.45 * (sliceAngle / 360);

  // Return label if text fits in slice, else return empty string
  return labelWidth > availableArcLength ? '' : labelText;
};
